import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Mail, Sparkles, TrendingUp, Newspaper, Gift } from 'lucide-react';
import { useT } from '../i18n';
import { getRef } from '../lib/referral';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/*
  Newsletter — wider, properly aligned home block. Submits to /api/newsletter
  via the Customer Engagement Service so it lands in the unified leads pipeline
  (List 4) without a dedicated backend endpoint per surface.
*/

export default function Newsletter() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  const items = isEs
    ? [
        { i: Newspaper, l: 'El resumen del lunes: "Esta semana en el sector"' },
        { i: Sparkles, l: 'Actualizaciones de IA' },
        { i: TrendingUp, l: 'Consejos de crecimiento' },
        { i: Gift, l: 'Recursos gratuitos' },
      ]
    : [
        { i: Newspaper, l: 'The Monday digest: "This Week in the Restaurant Sector"' },
        { i: Sparkles, l: 'AI updates' },
        { i: TrendingUp, l: 'Restaurant growth tips' },
        { i: Gift, l: 'Free resources' },
      ];

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    try {
      const res = await fetch(`${API_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          locale: lang,
          source_page: typeof window !== 'undefined' ? window.location.pathname : '/home-newsletter',
          ref: getRef() || '',
        }),
      });
      if (!res.ok) throw new Error('subscribe-failed');
      setStatus('success');
      setEmail('');
    } catch (err) {
      console.error('Newsletter subscription failed', err);
      setStatus('error');
    }
  };

  return (
    <section className="py-20 lg:py-24 bg-white" data-testid="newsletter-section">
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="relative rounded-3xl overflow-hidden border border-neutral-200 bg-gradient-to-br from-neutral-50 via-white to-orange-50/40"
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(circle at 18% 22%, #F77F11 0px, transparent 1.4px), radial-gradient(circle at 82% 78%, #F77F11 0px, transparent 1.4px)',
              backgroundSize: '120px 120px, 160px 160px',
            }}
          />

          <div className="relative grid lg:grid-cols-2 gap-10 lg:gap-16 p-8 sm:p-10 lg:p-14 items-center">
            <div>
              <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-semibold uppercase mb-3">
                {isEs ? 'Boletín' : 'Newsletter'}
              </p>
              <h2 className="text-3xl sm:text-4xl font-semibold text-neutral-900 tracking-[-0.015em] leading-tight">
                {isEs ? 'Ideas de Tecnología para Restaurantes.' : 'Restaurant Technology Insights.'}
              </h2>
              <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed max-w-md">
                {isEs
                  ? 'Recibe cada semana lo esencial para crecer en operaciones, IA y marketing.'
                  : 'A weekly read for restaurant operators on growth, AI and technology.'}
              </p>
              <ul className="mt-7 grid grid-cols-2 gap-x-5 gap-y-3">
                {items.map((it) => (
                  <li key={it.l} className="flex items-center gap-2.5 text-[14px] text-neutral-700 font-medium">
                    <span className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
                      <it.i className="w-3.5 h-3.5 text-[#F77F11]" />
                    </span>
                    {it.l}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <form
                onSubmit={onSubmit}
                className="rounded-2xl bg-white border border-neutral-200 p-5 sm:p-6 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.18)]"
                data-testid="newsletter-form"
              >
                <label htmlFor="news-email" className="block text-[12px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                  {isEs ? 'Tu correo' : 'Your email'}
                </label>
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <div className="flex-1 flex items-center gap-2 bg-neutral-50 border border-neutral-200 rounded-xl px-3 h-12 focus-within:border-[#F77F11] transition-colors">
                    <Mail className="w-4 h-4 text-neutral-400" />
                    <input
                      id="news-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@restaurant.com"
                      className="flex-1 bg-transparent outline-none text-[14px] placeholder:text-neutral-400 text-neutral-800"
                      data-testid="newsletter-email"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={status === 'loading' || status === 'success'}
                    className="inline-flex items-center justify-center gap-1 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-6 rounded-xl text-[14px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    data-testid="newsletter-submit"
                  >
                    {status === 'success'
                      ? isEs ? '¡Listo!' : 'Subscribed!'
                      : status === 'loading'
                      ? isEs ? 'Enviando…' : 'Sending…'
                      : isEs ? 'Suscribirme' : 'Subscribe'}
                    {status !== 'success' && <ArrowRight className="w-4 h-4 ml-0.5" />}
                    {status === 'success' && <Check className="w-4 h-4 ml-0.5" />}
                  </button>
                </div>
                {status === 'success' && (
                  <p className="mt-3 text-[13px] text-emerald-600 font-medium" data-testid="newsletter-success">
                    {isEs ? 'Gracias, te escribiremos pronto.' : 'Thanks — you\'ll hear from us soon.'}
                  </p>
                )}
                {status === 'error' && (
                  <p className="mt-3 text-[13px] text-red-600 font-medium" data-testid="newsletter-error">
                    {isEs
                      ? 'No pudimos suscribirte. Escríbenos a hello@kitxens.com.'
                      : "We couldn't subscribe you — please email hello@kitxens.com."}
                  </p>
                )}
                <p className="mt-4 text-[12px] text-neutral-500 leading-relaxed">
                  {isEs
                    ? 'Sin spam. Cancela cuando quieras.'
                    : 'No spam. Unsubscribe any time.'}
                </p>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
